<template>
  <div class="editor">
    <div class="editor-title">
      <p>
        <b>{{ title }}</b>
      </p>
    </div>
    <div class="buttons" v-if="editor">
      <!-- <div class="menu-button" @click="$emit('chatgpt')">
        <bg-icon name="fbold"></bg-icon>
      </div> -->
      <div
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleBold()
            .run()
        "
        :class="{ 'is-active': editor.isActive('bold') }"
      >
        <bg-icon name="fbold"></bg-icon>
      </div>
      <div
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleItalic()
            .run()
        "
        :class="{ 'is-active': editor.isActive('italic') }"
      >
        <bg-icon name="fitalic"></bg-icon>
      </div>
      <div
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleUnderline()
            .run()
        "
        :class="{ 'is-active': editor.isActive('underline') }"
      >
        <bg-icon name="funderline"></bg-icon>
      </div>

      <div
        v-if="extended"
        style="margin-left: 5px;"
        class="menu-button"
        @click="trigger"
      >
        <input
          ref="imageRef"
          type="file"
          @change="onFileChange"
          accept="image/*"
        />
        <bg-icon name="image"></bg-icon>
      </div>

      <div
        v-if="!editor.isActive('link')"
        style="margin-left: 5px;"
        class="menu-button"
        @click="setLink"
        :class="{ 'is-active': editor.isActive('link') }"
      >
        <bg-icon name="link"></bg-icon>
      </div>
      <div
        v-if="extended && editor.isActive('link')"
        style="margin-left: 5px;"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .unsetLink()
            .run()
        "
      >
        <p style="color: white;">X</p>
      </div>

      <div
        v-if="extended"
        style="margin-left: 5px;"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 1 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 1 }) }"
      >
        <p style="color: white;">H1</p>
      </div>
      <div
        v-if="extended"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 2 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 2 }) }"
      >
        <p style="color: white;">H2</p>
      </div>
      <div
        v-if="extended"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleHeading({ level: 3 })
            .run()
        "
        :class="{ 'is-active': editor.isActive('heading', { level: 3 }) }"
      >
        <p style="color: white;">H3</p>
      </div>

      <div
        v-if="extended"
        style="margin-left: 5px;"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .setHorizontalRule()
            .run()
        "
      >
        <p style="color: white;">--</p>
      </div>

      <div
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .toggleBulletList()
            .run()
        "
        :class="{ 'is-active': editor.isActive('bulletList') }"
      >
        <bg-icon name="flist"></bg-icon>
      </div>
      <div
        style="margin-left: 5px;"
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .undo()
            .run()
        "
      >
        <bg-icon name="fundo"></bg-icon>
      </div>
      <div
        class="menu-button"
        @click="
          editor
            .chain()
            .focus()
            .redo()
            .run()
        "
      >
        <bg-icon name="fredo"></bg-icon>
      </div>
      <div class="lottie-wrapper">
        <lottie
          :options="defaultOptions"
          :height="40"
          :width="40"
          v-on:animCreated="handleAnimation"
          style="margin-right: 0;"
        />
      </div>
    </div>
    <editor-content
      :class="{ 'white-background': whiteBackground }"
      :editor="editor"
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import BgIcon from "@/components/bg-icon";
import Underline from "@tiptap/extension-underline";
import Lottie from "vue-lottie";
import * as animationData from "./check.json";
import Link from "@tiptap/extension-link";

export default {
  props: {
    loadContent: {
      type: String,
    },
    extended: {
      type: Boolean,
      default: false,
    },
    whiteBackground: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    autohide: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EditorContent,
    BgIcon,
    Lottie,
  },
  data() {
    return {
      editor: null,
      content: "",
      defaultOptions: {
        animationData: animationData.default,
        loop: false,
        autoplay: false,
      },
      animationSpeed: 1,
    };
  },
  methods: {
    addImage() {
      const url = window.prompt("URL");

      if (url) {
        this.editor
          .chain()
          .focus()
          .setImage({ src: url })
          .run();
      }
    },
    handleAnimation: function(anim) {
      this.anim = anim;
    },
    clearEditor() {
      this.editor.commands.clearContent(true);
    },
    async onFileChange(e) {
      const url = await this.toBase64(this.$refs.imageRef.files[0]);
      this.editor
        .chain()
        .focus()
        .setImage({ src: url })
        .run();
    },
    setLink() {
      const url = window.prompt("URL");

      this.editor
        .chain()
        .focus()
        .setLink({ href: url })
        .run();
    },
    setContent(input) {
      this.editor.commands.setContent(input);
    },
    start() {
      this.anim.play();
    },
    stop() {
      this.anim.stop();
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    trigger() {
      this.$refs.imageRef.click();
    },
  },
  mounted() {
    const vm = this;
    this.editor = new Editor({
      extensions: [StarterKit, Image, Underline, Link],
      editorProps: {
        attributes: {
          class: "editor-content",
        },
      },
      content: this.loadContent,
      onBlur() {
        vm.$emit("update", vm.content);
        vm.start();
        setTimeout(function() {
          vm.stop();
        }, 1800);
      },
      onUpdate() {
        const html = this.getHTML();
        vm.content = html;
      },
      onFocus() {
        const html = this.getHTML();
        vm.content = html;
      },
    });
  },
  beforeDestroy() {
    this.editor.destroy();
  },
};
</script>

<style lang="scss">
.editor {
  margin-top: 20px;
}

.editor-content {
  padding: 25px;
}

.editor-title {
  margin-bottom: 5px;
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  min-height: 40px;
}

.white-background {
  background-color: white;
  border-radius: 6px;
  margin-top: 10px;
}

.menu-button {
  background-color: #636383;
  border-radius: 6px;
  width: 40px;
  min-width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: default;

  svg {
    padding: 3px;
    color: white;
  }

  &:hover {
    background-color: #5a5a79;
    box-shadow: 0px 0px 0px 3px rgba(209, 219, 229, 1);
  }

  &.is-active {
    background-color: #7f7f9f;
  }

  input {
    position: absolute;
    left: 0;
    top: 0;
    opacity: 0;
    width: 0.1px;
    border: 2px solid red;
  }
}

/* Basic editor styles */
.ProseMirror {
  > * + * {
    margin-top: 0.75em;
  }

  a {
    color: #0931f0;
  }

  ul,
  ol {
    padding: 0 1rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
  }

  code {
    background-color: rgba(#616161, 0.1);
    color: #616161;
  }

  pre {
    background: #0d0d0d;
    color: #fff;
    font-family: "JetBrainsMono", monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0d0d0d, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0d0d0d, 0.1);
    margin: 2rem 0;
  }
}
.ProseMirror:focus {
  outline: none;
}

.ProseMirror li {
  list-style-type: circle;
  list-style-position: inside;
}

.ProseMirror li p {
  display: inline;
}
</style>
